// src/components/shared/OfflineBanner.js
// Banner component to show offline/online status

import React from "react";
import { WifiOff, Wifi } from "lucide-react";
import { useOnlineStatus } from "../../hooks";
import { useTranslation } from "react-i18next";

const styles = {
  banner: {
    position: "fixed",
    top: 0,
    left: 0,
    right: 0,
    zIndex: 9999,
    padding: "10px 16px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: "8px",
    fontSize: "14px",
    fontWeight: 500,
    transition: "transform 0.3s ease, opacity 0.3s ease",
  },
  offline: {
    backgroundColor: "#fef2f2",
    color: "#dc2626",
    borderBottom: "1px solid #fecaca",
  },
  online: {
    backgroundColor: "#f0fdf4",
    color: "#16a34a",
    borderBottom: "1px solid #bbf7d0",
  },
  hidden: {
    transform: "translateY(-100%)",
    opacity: 0,
    pointerEvents: "none",
  },
  visible: {
    transform: "translateY(0)",
    opacity: 1,
  },
  icon: {
    width: "18px",
    height: "18px",
  },
};

export default function OfflineBanner() {
  const { isOnline, wasOffline } = useOnlineStatus();
  const { t } = useTranslation();

  // Don't render anything if online and wasn't recently offline
  const shouldShow = !isOnline || wasOffline;

  if (!shouldShow) {
    return null;
  }

  const bannerStyle = {
    ...styles.banner,
    ...(isOnline ? styles.online : styles.offline),
    ...styles.visible,
  };

  return (
    <div style={bannerStyle} role="alert" aria-live="polite">
      {isOnline ? (
        <>
          <Wifi style={styles.icon} />
          <span>{t("offlineBanner.backOnline", "Back online")}</span>
        </>
      ) : (
        <>
          <WifiOff style={styles.icon} />
          <span>{t("offlineBanner.offlineMessage", "You're offline. Some features may be unavailable.")}</span>
        </>
      )}
    </div>
  );
}
